"use client"

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { Activity, Gauge, LayoutGrid, ShoppingBag, TrendingUp } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

const MOVEMENT_COLORS = { High: "#16a34a", Medium: "#d97706", Low: "#64748b", Dead: "#dc2626" }

const STOCK_RATE_COLORS = {
  Healthy: "#16a34a",
  "Reorder Required": "#d97706",
  "Running Out Soon": "#ea580c",
  "Out of Stock": "#dc2626",
  "Non-Moving": "#64748b",
}

const PERIOD_COLORS = { "7D": "#0263cc", "30D": "#7c3aed", "60D": "#0891b2", "90D": "#059669" }

const ITEM_LINE_COLORS = ["#0263cc", "#7c3aed", "#0891b2", "#059669", "#d97706"]

const truncate = (str, max = 12) => (str && str.length > max ? `${str.slice(0, max)}…` : str || "—")

function ChartCard({ title, icon: Icon, children }) {
  return (
    <Card className="border border-gray-200 shadow-sm rounded-xl overflow-hidden">
      <CardHeader className="py-3 px-4 border-b border-gray-100 bg-gray-50/60">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold text-gray-700">
          {Icon && <Icon className="h-4 w-4 text-[#0263cc]" />}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3">{children}</CardContent>
    </Card>
  )
}

function LoadingState() {
  return (
    <div className="h-[220px] flex items-center justify-center">
      <div className="animate-spin w-5 h-5 border-2 border-[#0263cc] border-t-transparent rounded-full" />
    </div>
  )
}

function EmptyState({ message }) {
  return <div className="h-[220px] flex items-center justify-center text-sm text-gray-400">{message}</div>
}

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
      {label && <p className="font-medium text-gray-700 mb-1">{label}</p>}
      {payload.map((p) => (
        <p key={`${p.dataKey}-${p.name}`} style={{ color: p.color || p.fill }} className="text-xs">
          {p.name}: <span className="font-semibold">{p.value}</span>
        </p>
      ))}
    </div>
  )
}

export default function InventoryAnalyticsCharts({ charts, loading, isSubCategoryEnabled, daysFilter }) {
  const { movementDistribution, stockRateDistribution, unitsSoldByCategory, movementByGroup, velocityTrend, activePeriodKey } = charts

  return (
    <div className="p-4 space-y-4 bg-gray-50/40">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <ChartCard title="Movement" icon={Activity}>
          {loading ? (
            <LoadingState />
          ) : movementDistribution.length === 0 ? (
            <EmptyState message="No movement data" />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={movementDistribution} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                  {movementDistribution.map((entry) => (
                    <Cell key={entry.name} fill={MOVEMENT_COLORS[entry.name] || "#94a3b8"} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Rate of Stock" icon={Gauge}>
          {loading ? (
            <LoadingState />
          ) : stockRateDistribution.length === 0 ? (
            <EmptyState message="No stock data" />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={stockRateDistribution} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                  {stockRateDistribution.map((entry) => (
                    <Cell key={entry.name} fill={STOCK_RATE_COLORS[entry.name] || "#94a3b8"} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title={`Movement by ${isSubCategoryEnabled ? "Sub-category" : "Category"}`} icon={LayoutGrid}>
          {loading ? (
            <LoadingState />
          ) : movementByGroup.length === 0 ? (
            <EmptyState message="No data available" />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={movementByGroup} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  tickFormatter={(v) => truncate(v, 8)}
                  interval={0}
                  angle={-20}
                  textAnchor="end"
                  height={50}
                />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="High" name="High" stackId="movement" fill={MOVEMENT_COLORS.High} />
                <Bar dataKey="Medium" name="Medium" stackId="movement" fill={MOVEMENT_COLORS.Medium} />
                <Bar dataKey="Low" name="Low" stackId="movement" fill={MOVEMENT_COLORS.Low} />
                <Bar dataKey="Dead" name="Dead" stackId="movement" fill={MOVEMENT_COLORS.Dead} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Units Sold — 7D / 30D / 60D / 90D by Category" icon={ShoppingBag}>
          {loading ? (
            <LoadingState />
          ) : unitsSoldByCategory.length === 0 ? (
            <EmptyState message="No sales data" />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={unitsSoldByCategory} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis
                  dataKey="category"
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  tickFormatter={(v) => truncate(v, 10)}
                  interval={0}
                  angle={-20}
                  textAnchor="end"
                  height={50}
                />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {["7D", "30D", "60D", "90D"].map((period) => (
                  <Bar
                    key={period}
                    dataKey={period}
                    name={period}
                    fill={PERIOD_COLORS[period]}
                    fillOpacity={activePeriodKey === period ? 1 : 0.3}
                    radius={[4, 4, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Velocity Trend — Top Items" icon={TrendingUp}>
          {loading ? (
            <LoadingState />
          ) : velocityTrend.series.length === 0 ? (
            <EmptyState message="No movement history yet" />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={velocityTrend.data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="period" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} formatter={(value) => truncate(value, 16)} />
                {velocityTrend.series.map((item, i) => (
                  <Line
                    key={item.key}
                    type="monotone"
                    dataKey={item.key}
                    name={item.name}
                    stroke={ITEM_LINE_COLORS[i % ITEM_LINE_COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>
    </div>
  )
}
